import { generateNKeysBetween } from "fractional-indexing";
import { useSyncExternalStore } from "react";
import LocalStoragePersistence from "../../localStoragePersistence/common/LocalStoragePersistence";
import { LocalStorageKey } from "../../localStoragePersistence/common/LocalStorageKey";
import PlaylistData from "../data/PlaylistData";

/**
 * A preview opened from a link or QR code. Only the ids are kept, never the playlist itself (ADR-11): the playlist
 * from the cache replaces the preview as soon as it is there.
 */
export interface RecentPreview {
  readonly id: string;
  readonly songIds: readonly string[];
  readonly openedAt: string;
}

export const maxRecentPreviews = 5;

const recentPreviewsKey = new LocalStorageKey("Playlist", "recentPreviews");
const listeners = new Set<() => void>();
let recentPreviews: readonly RecentPreview[] | undefined;

function isRecentPreview(value: unknown): value is RecentPreview {
  const v = value as RecentPreview;
  return (
    typeof v?.id === "string" &&
    typeof v.openedAt === "string" &&
    Array.isArray(v.songIds) &&
    v.songIds.every((s) => typeof s === "string")
  );
}

/** Newest first. Unversioned: whatever cannot be read is dropped. */
export function loadRecentPreviews(): readonly RecentPreview[] {
  if (recentPreviews === undefined) {
    const stored =
      LocalStoragePersistence.read<unknown>(recentPreviewsKey).unwrapOr(
        undefined,
      );
    recentPreviews = Array.isArray(stored)
      ? stored.filter(isRecentPreview)
      : [];
  }
  return recentPreviews;
}

function save(previews: readonly RecentPreview[]) {
  recentPreviews = previews;
  LocalStoragePersistence.persist(recentPreviewsKey, previews);
  listeners.forEach((l) => l());
}

export function rememberRecentPreview(id: string, songIds: readonly string[]) {
  save(
    [
      { id, songIds, openedAt: new Date().toISOString() },
      ...loadRecentPreviews().filter((p) => p.id !== id),
    ].slice(0, maxRecentPreviews),
  );
}

export function forgetRecentPreviews(ids: readonly string[]) {
  const previews = loadRecentPreviews();
  if (previews.some((p) => ids.includes(p.id))) {
    save(previews.filter((p) => !ids.includes(p.id)));
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useRecentPreviews(): readonly RecentPreview[] {
  return useSyncExternalStore(subscribe, loadRecentPreviews);
}

export function toPreviewPlaylist(
  preview: RecentPreview,
  title: string,
): PlaylistData {
  const openedAt = new Date(preview.openedAt);
  const positions = generateNKeysBetween(null, null, preview.songIds.length);
  return new PlaylistData(
    preview.id,
    title,
    undefined, //previews do not belong to a crew
    [],
    preview.songIds.map((songEntryId, index) => ({
      id: `preview-${index}`,
      position: positions[index],
      songEntryId,
    })),
    openedAt,
    openedAt,
    true,
  );
}
